import { ClipboardList, MessageSquare, Package, LifeBuoy, Users, CalendarClock, CircleHelp, type LucideIcon } from 'lucide-react';

/**
 * Activity categories for the notification history drawer filter chips.
 * Kept OUT of notifications.tsx so component files stay component-only
 * (react-refresh/only-export-components) and the classifier is reusable.
 *
 * Categories are grounded in the actual producers (audit 2026-09-30):
 * - trace: TraceSplitScreenModal.tsx (chat @-mentions + assigned users)
 * - chat-reply: TraceSplitScreenModal / FundsachenChatPanel /
 *   SupportTicketModal (reply to YOUR message; entityType trace/fundsachen/ticket)
 * - lost_and_found: FundsachenChatPanel (mentions + responsible party)
 * - support-ticket-reply: api/admin.js (admin ticket reply)
 * - storno-conflict: api/dedge-storno-scan.js (D-Edge storno scan)
 * - hr_absence / hr_absence_reply / hr_request (legacy): useHRData.ts,
 *   useAdminHRData.ts (absence request / admin decision)
 * - default: api/services.js writes `type: type || "default"` for producers
 *   that pass no type (waiting list "room free", overbooking auto-lock).
 *   Chat FCM pushes (type "chat-message") deliberately write NO row.
 */
export type ActivityCategory = "trace" | "chat-reply" | "lost_and_found" | "ticket" | "storno" | "hr" | "other";

/** Category config: label i18n key + lucide icon + active-chip classes. */
export interface ActivityCategoryConfig {
  labelKey: string;
  Icon: LucideIcon;
  /** Prebuilt Tailwind classes for the ACTIVE chip state. Static strings:
   *  the Tailwind JIT extracts class names from source text, so runtime-
   *  interpolated arbitrary values (e.g. `var(${x})`) would emit broken CSS
   *  (build failure "Unexpected token Delim"). Colors are the design-system
   *  tokens from index.css (ui-standards-adherence). */
  activeClasses: string;
}

export const ACTIVITY_CATEGORIES: Record<ActivityCategory, ActivityCategoryConfig> = {
  trace: { labelKey: "notifications.activityFilterTrace", Icon: ClipboardList, activeClasses: "bg-accent border-accent text-white" },
  "chat-reply": { labelKey: "notifications.activityFilterChatReply", Icon: MessageSquare, activeClasses: "bg-info border-info text-white" },
  lost_and_found: { labelKey: "notifications.activityFilterLostFound", Icon: Package, activeClasses: "bg-warn border-warn text-white" },
  ticket: { labelKey: "notifications.activityFilterTicket", Icon: LifeBuoy, activeClasses: "bg-indigo border-indigo text-white" },
  storno: { labelKey: "notifications.activityFilterStorno", Icon: CalendarClock, activeClasses: "bg-err border-err text-white" },
  hr: { labelKey: "notifications.activityFilterHr", Icon: Users, activeClasses: "bg-purple border-purple text-white" },
  other: { labelKey: "notifications.activityFilterOther", Icon: CircleHelp, activeClasses: "bg-muted border-muted text-white" },
};

/**
 * Minimal row shape the classifier needs (structural — avoids importing the
 * component file, which would create a cycle with notifications.tsx).
 */
export interface ClassifiableNotificationRow {
  type?: string;
  data?: { type?: string; [key: string]: unknown } | null;
}

/**
 * Resolves the activity category of one notification row. Reads the raw
 * type from the column first, then the jsonb mirror (legacy rows), and
 * buckets legacy/untyped values into sensible categories.
 *
 * @param n - The notification row (top-level column or jsonb `data` mirror).
 * @returns The activity category for chips and row badges.
 */
export const getActivityCategory = (n: ClassifiableNotificationRow): ActivityCategory => {
  const rawType = (n.type || n.data?.type || "default").toString();
  if (rawType === "trace") return "trace";
  // Untyped producers (waiting list, overbooking locks) land in "other".
  if (rawType === "default") return "other";
  if (rawType === "chat-reply") return "chat-reply";
  if (rawType === "lost_and_found") return "lost_and_found";
  if (rawType === "support-ticket-reply" || rawType === "ticket") return "ticket";
  if (rawType === "storno-conflict") return "storno";
  if (rawType === "hr_absence" || rawType === "hr_absence_reply" || rawType === "hr_request") return "hr";
  // Unknown future types land in "other" instead of silently vanishing
  // when a specific filter is active.
  return "other";
};
